import { useEffect, useState } from 'react';
import { Modal, Table, Button, message, Tooltip, Icon } from 'antd';
import { FormDetail } from '../../FormManager/components';
import { useTranslation } from 'react-i18next';
import { getTemplateVersionsPromise, setTemplateVersionPromise, createTemplateVersionPromise } from '../promises/formManagerPromises';
import moment from "moment/moment";

const VersionHistoryModal = ({ currentUser, template, onClose, handleEditTemplate }) => {
    const { t } = useTranslation();
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [isActive, setIsActive] = useState(false);
    const [showVersion, setShowVersion] = useState(null)
    const [hasDraft, setHasDraft] = useState(false)

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        setLoading(true);
        setHasDraft(false)
        getTemplateVersionsPromise(template.id)
            .then((data) => {
                const versions = data.versionInfo
                setVersions(versions);
                setIsActive(data.active)

                const draft = versions.filter(X => X.status === 'DRAFT').length;
                setHasDraft(draft > 0)

                const actual = versions.find(X => X.version === data.version);
                setCurrentVersion(actual?.version);
            })
            .catch(() => message.error('Error al obtener versiones'))
            .finally(() => setLoading(false));
    }

    const handleSelectVersion = (version) => {
        Modal.confirm({
            title: t('messages.aml.form.template.version.history.action.use.this.version'),
            content: t('messages.aml.form.template.version.history.action.confirm.this.version'),
            okText: t('messages.aml.confirm'),
            cancelText: t('messages.aml.cancel'),
            onOk: () => {
                setLoading(true);
                setTemplateVersionPromise(template.id, version)
                    .then(() => {
                        message.success(t('messages.aml.form.template.version.history.success.updated'));
                        load();
                    })
                    .catch(() => message.error(t('messages.aml.form.template.version.history.error.updating')))
                    .finally(() => setLoading(false));
            }
        })
    };

    const handleCreateVersion = () => {
        Modal.confirm({
            title: t('messages.aml.form.template.create.version.title'),
            content: t('messages.aml.form.template.create.version.content'),
            okText: t('messages.aml.confirm'),
            cancelText: t('messages.aml.cancel'),
            onOk: () => {
                setLoading(true);
                createTemplateVersionPromise(template.id)
                    .then(() => {
                        message.success(t('messages.aml.form.template.created.version.ok'));
                        handleEditTemplate(template);
                        load()
                    })
                    .catch(() => message.error(t('messages.aml.form.template.version.history.error.updating')))
                    .finally(() => setLoading(false));
            }
        })
    };

    const handleShowVersion = (version) => {
        setShowVersion({ templateVersionId: version.id, templateId: version.formId})
    }

    const onCancelShowVersion =() => {
        setShowVersion(null)
    }

    const getColumns = () => {
        const columns = [
            {
                title: t('forms.table.version', {ns: 'forms'}),
                dataIndex: 'version',
            },
            {
                title: t('forms.table.author', {ns: 'forms'}),
                dataIndex: 'autor',
            },
            {
                title: t('forms.table.status', {ns: 'forms'}),
                dataIndex: 'status',
                render: (val) => t('messages.aml.form.template.status.' + val)
            },
            {
                title: t('forms.table.date', {ns: 'forms'}),
                dataIndex: 'fecha',
                render: (val) => {
                    return  moment(val).format('DD-MM-YYYY HH:mm:ss')
                },
            },
            {
                title: t('forms.table.preview', {ns: 'forms'}),
                dataIndex: 'objJson',
                align: 'center',
                render: (text, record) => (
                    <Tooltip title={t('messages.aml.view', {ns: 'translations'})}>
                        <Icon type="eye" onClick={() => handleShowVersion(record)}/>
                    </Tooltip>
                ),
            }
        ];
        if(currentUser.type !== 'AUDIT') {
            columns.push(
                {
                    title: t('forms.table.action', {ns: 'forms'}),
                    render: (record) =>
                        isActive && (
                            record.version !== currentVersion ? 
                                <>
                                    <Button style={{marginLeft:5}} size="small" onClick={() => handleSelectVersion(record.version)}>
                                        { record.status === 'DRAFT' ? 
                                            t('messages.aml.form.template.version.history.action.publish')
                                            :
                                            t('messages.aml.form.template.version.history.action.use.this.version')
                                        }
                                    </Button>
                                    { record.status === 'DRAFT' &&
                                        <Button style={{marginLeft:5}} size="small" onClick={() => handleEditTemplate(template, record)}>
                                            {t('messages.aml.modify')}
                                        </Button>
                                    }
                                </>
                            : (
                                !hasDraft &&
                                    <Tooltip title={t('forms.tooltip.create_version_from_last_changes', {ns: 'forms'})}>
                                    <Button type="primary" style={{marginLeft:5}} size="small" onClick={handleCreateVersion}>
                                        {t('messages.aml.form.template.create.version.title')}
                                    </Button>
                                    </Tooltip>
                            )
                        )
                }
            )
        }
        return columns;
    }

    return (
        <>
            <Modal
                visible={true}
                title={t('messages.aml.form.modal.version.history')}
                onCancel={onClose}
                footer={null}
                width={1000}
                className="form-manager-versions-history"
            >
                <Table
                    rowKey="id"
                    columns={getColumns()}
                    dataSource={versions}
                    loading={loading}
                    pagination={false}
                    size="small"
                    rowClassName={(record) => "version-status-" + record.status }
                />
            </Modal>
            {showVersion &&
                <Modal
                    title="Previsualizar"
                    visible={true}
                    width={"97vw"}
                    bodyStyle={{minHeight: 'calc(100vh - 120px)', overflowY: 'auto'}}
                    style={{ top: "5px" }}
                    onCancel={onCancelShowVersion}
                    footer={<Button type="primary" onClick={onCancelShowVersion}>{t('messages.aml.form.template.close', {ns: 'forms'})}</Button>}
                >
                    <FormDetail currentUser={currentUser} templateId={showVersion.templateId} templateVersionId={showVersion.templateVersionId} />
                </Modal>
            }
        </>
    );
};

export default VersionHistoryModal;
